import { useCallback, useState } from 'react'
import type { DataTablePagination, TablePaginationProps } from '@atrvd/saas-ui'
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  buildDataTablePagination,
  isPaginationVisible,
  lastPageOf,
  type PaginationState,
} from '@/lib/pagination'

/** Options of {@link useTablePagination}. */
export interface UseTablePaginationOptions {
  /**
   * Rows across all pages: `rows.length` for a client-sliced table, a separate
   * count query's result for a server-paged one. `undefined` while that count
   * is not known yet — the page is then left alone (no clamp) and the footer
   * hidden. Also `undefined` for a table whose total arrives in the same
   * response as the page: that query needs `page`/`pageSize` from this hook
   * first, so its total goes through {@link TablePaginationState.forTotal}.
   */
  totalItems: number | undefined
  /**
   * The page goes back to 1 whenever this value changes. Build it from the
   * table's filters and sort (e.g. `JSON.stringify([search, status, sort])`):
   * page 3 of a different result set is an arbitrary slice. Omit for a table
   * without filters or sort.
   */
  resetKey?: string
}

/** The footer props of one table; both are empty when the footer is hidden. */
export interface TablePaginationFooter {
  /** The saas-ui `DataTable` `pagination` prop; `undefined` when the footer is hidden. */
  dataTablePagination: DataTablePagination | undefined
  /**
   * Props for the standalone saas-ui `TablePagination` footer (tables that are
   * not a `DataTable`); `null` when the footer is hidden. Add `itemName` yourself.
   */
  tablePaginationProps: Omit<TablePaginationProps, 'itemName' | 'className'> | null
}

/** Input of {@link TablePaginationState.forTotal}: the page query's total and whether it is settled. */
export interface PageQueryTotal {
  /** Rows across all pages as the latest answer (placeholder included) reports it; `undefined` before any. */
  total: number | undefined
  /** The page query's `isFetching`: while true the total may belong to another page or an older result. */
  isFetching: boolean
}

/** What {@link TablePaginationState.forTotal} returns: the footer and the total it was built from. */
export interface PageQueryFooter extends TablePaginationFooter {
  /**
   * `total`, or while it is `undefined` the last settled total under the
   * current `resetKey`. A page whose request failed has no data at all, so this
   * keeps the count and the footer of the set the user is paging through.
   * `undefined` before the first answer and after a filter / sort change.
   */
  knownTotal: number | undefined
}

/** What {@link useTablePagination} returns. */
export interface TablePaginationState extends TablePaginationFooter {
  /** Current 1-based page, always within `1..lastPage`. */
  page: number
  pageSize: number
  /** Rows to skip — the `offset` of a server-paged query. */
  offset: number
  setPage: (page: number) => void
  /** Changes the size and goes back to page 1. */
  setPageSize: (size: number) => void
  /** The current page's slice of `rows` — for tables that hold every row client-side. */
  paginate: <T>(rows: T[]) => T[]
  /**
   * The footer for a server-paged table whose total comes back WITH the page
   * (`{ items, total }`, a `total_count` column). Call it during render, after
   * the page query, with that query's total and `isFetching`.
   *
   * - The footer is built from `total` even while fetching, so with
   *   `placeholderData` it stays put while the next page loads.
   * - The out-of-range clamp (a shrunk result leaving the user past the last
   *   page) runs only on a settled answer: while fetching, the total may be an
   *   older one that does not reach a page just opened on purpose (e.g. the
   *   page of a row just created).
   * - While `total` is `undefined` (a page that failed), the footer uses the
   *   last settled total of the same `resetKey` (`knownTotal`), so the user
   *   can step back to a page that loaded.
   *
   * Like the `totalItems` clamp it adjusts state during render, so the
   * out-of-range page never paints.
   */
  forTotal: (pageQuery: PageQueryTotal) => PageQueryFooter
}

interface PaginationSlots {
  page: number
  pageSize: number
  resetKey: string | undefined
  /** The last total `forTotal` saw under `resetKey`. */
  lastTotal: number | undefined
}

/**
 * Local (not persisted) page + page-size state for one table, following the
 * app-wide rules in `lib/pagination.ts`: sizes 20/50/100, default 20, footer
 * hidden at ≤ 20 records.
 *
 * - A size change goes back to page 1.
 * - When `totalItems` shrinks under the current page (a row deleted on the
 *   last page, a refetch), the page moves to the new last page.
 * - When `resetKey` changes (filters / sort), the page goes back to 1.
 *
 * Both corrections happen during render, not in an effect, so no frame ever
 * shows an out-of-range page.
 *
 * Where the total arrives in the same response as the page, pass
 * `totalItems: undefined` and take the footer from `forTotal` after the page
 * query. For page/size kept in the URL, use the `lib/pagination.ts` helpers
 * directly (see `useBankingFilterParams`).
 */
export function useTablePagination({ totalItems, resetKey }: UseTablePaginationOptions): TablePaginationState {
  const [slots, setSlots] = useState<PaginationSlots>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    resetKey,
    lastTotal: undefined,
  })

  // Adjusting state while rendering (React's documented alternative to an
  // effect): React re-renders before committing, so the stale page never paints.
  const resetPending = slots.resetKey !== resetKey
  const unclamped = resetPending ? 1 : slots.page
  const page = totalItems === undefined ? unclamped : Math.min(unclamped, lastPageOf(totalItems, slots.pageSize))
  if (resetPending || page !== slots.page) {
    // Another filter set: the last total belongs to the old one.
    setSlots({ page, pageSize: slots.pageSize, resetKey, lastTotal: resetPending ? undefined : slots.lastTotal })
  }

  const { pageSize } = slots
  const offset = (page - 1) * pageSize

  const setPage = useCallback((next: number) => {
    setSlots((prev) => ({ ...prev, page: Math.max(1, next) }))
  }, [])
  const setPageSize = useCallback((size: number) => {
    setSlots((prev) => ({ ...prev, page: 1, pageSize: size }))
  }, [])
  const paginate = useCallback(<T>(rows: T[]): T[] => rows.slice(offset, offset + pageSize), [offset, pageSize])

  const footerFor = (total: number | undefined): TablePaginationFooter =>
    buildFooter({ page, pageSize, totalItems: total, onPageChange: setPage, onPageSizeChange: setPageSize })

  const forTotal = ({ total, isFetching }: PageQueryTotal): PageQueryFooter => {
    if (!isFetching && total !== undefined && page > lastPageOf(total, pageSize)) {
      // Same render-time adjustment as above; the footer returned with the
      // stale page is discarded by the re-render this triggers.
      setSlots((prev) => ({ ...prev, page: lastPageOf(total, prev.pageSize) }))
    }
    // Only a settled answer: while fetching, `total` may be the placeholder of
    // the previous filter set.
    if (!isFetching && !resetPending && total !== undefined && total !== slots.lastTotal) {
      setSlots((prev) => ({ ...prev, lastTotal: total }))
    }
    const knownTotal = total ?? (resetPending ? undefined : slots.lastTotal)
    return { ...footerFor(knownTotal), knownTotal }
  }

  return {
    page,
    pageSize,
    offset,
    setPage,
    setPageSize,
    paginate,
    forTotal,
    ...footerFor(totalItems),
  }
}

/** Both footer shapes for one state; hidden while the total is unknown or ≤ 20. */
function buildFooter({
  totalItems,
  ...state
}: Omit<PaginationState, 'totalItems'> & { totalItems: number | undefined }): TablePaginationFooter {
  if (totalItems === undefined || !isPaginationVisible(totalItems)) {
    return { dataTablePagination: undefined, tablePaginationProps: null }
  }
  return {
    dataTablePagination: buildDataTablePagination({ ...state, totalItems }),
    tablePaginationProps: {
      currentPage: state.page,
      pageSize: state.pageSize,
      totalItems,
      onPageChange: state.onPageChange,
      pageSizeOptions: [...PAGE_SIZE_OPTIONS],
      onPageSizeChange: state.onPageSizeChange,
    },
  }
}
